<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { NCard, NButton } from "naive-ui";
import { useAuthStore } from "@/store/modules/auth";
import { useUserStore } from "@/store/modules/user";
import { $t } from "@/locales";
import { useAppStore } from "@/store/modules/app";

const route = useRoute();
const authStore = useAuthStore();
const userStore = useUserStore();
const appStore = useAppStore();
const direction = computed(() => appStore.direction);
const loading = ref(false);

const showOverlay = computed(() => authStore.showLoginOverlay);

const routeBenefits = computed(() => {
  const routeName = route.name as string;

  // Map route names to their i18n benefit keys
  const benefitsMap: Record<string, string[]> = {
    "affiliate": [
      $t("page.login.common.benefits.affiliate.a"),
      $t("page.login.common.benefits.affiliate.b"),
      $t("page.login.common.benefits.affiliate.c"),
    ],
    "api-keys": [
      $t("page.login.common.benefits.api-keys.a"),
      $t("page.login.common.benefits.api-keys.b"),
      $t("page.login.common.benefits.api-keys.c"),
    ],
    "billing_credit": [
      $t("page.login.common.benefits.billing.a"),
      $t("page.login.common.benefits.billing.b"),
      $t("page.login.common.benefits.billing.c"),
    ],
    "billing_history": [
      $t("page.login.common.benefits.billing.a"),
      $t("page.login.common.benefits.billing.b"),
      $t("page.login.common.benefits.billing.c"),
    ],
    "billing_packages": [
      $t("page.login.common.benefits.packages.a"),
      $t("page.login.common.benefits.packages.b"),
      $t("page.login.common.benefits.packages.c"),
    ],
    home: [
      $t("page.login.common.benefits.home.a"),
      $t("page.login.common.benefits.home.b"),
      $t("page.login.common.benefits.home.c"),
    ],
    models: [
      $t("page.login.common.benefits.models.a"),
      $t("page.login.common.benefits.models.b"),
      $t("page.login.common.benefits.models.c"),
    ],
    "usage_overview": [
      $t("page.login.common.benefits.usage.a"),
      $t("page.login.common.benefits.usage.b"),
      $t("page.login.common.benefits.usage.c"),
    ],
    "usage_reports": [
      $t("page.login.common.benefits.usage.a"),
      $t("page.login.common.benefits.usage.b"),
      $t("page.login.common.benefits.usage.c"),
    ],
    "support_dashboard": [
      $t("page.login.common.benefits.support.a"),
      $t("page.login.common.benefits.support.b"),
      $t("page.login.common.benefits.support.c"),
    ],
    "support_my-tickets": [
      $t("page.login.common.benefits.support.a"),
      $t("page.login.common.benefits.support.b"),
      $t("page.login.common.benefits.support.c"),
    ],
    "support_create-ticket": [
      $t("page.login.common.benefits.support.a"),
      $t("page.login.common.benefits.support.b"),
      $t("page.login.common.benefits.support.c"),
    ],
    "support_knowledge-base": [
      $t("page.login.common.benefits.support.a"),
      $t("page.login.common.benefits.support.b"),
      $t("page.login.common.benefits.support.c"),
    ],
  };

  return benefitsMap[routeName] || [];
});

const handleLogin = () => {
  loading.value = true;
  // Store the current route for post-login redirect
  authStore.setOriginalRoute(route.fullPath);
  authStore.loginWithRedirect();
};

const handleDismiss = () => {
  authStore.setOverlayVisibility(false);
  userStore.setGuestMode(true);
  userStore.setGuestSessionStart();

  // Show guest mode banner
  authStore.setGuestBannerVisibility(true);
};

const handleBackdropClick = () => {
  // Allow dismissal by clicking backdrop
  handleDismiss();
};
</script>

<template>
  <Teleport to="body">
    <div v-if="showOverlay" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="handleBackdropClick">
      <div class="relative max-w-md w-full mx-4">
        <NCard class="relative" :bordered="false" size="large" closable @close="handleDismiss">
          <!-- Header -->
          <template #header>
            <div class="text-center">
              <h2 class="text-xl font-semibold">
                {{ $t("page.login.common.needLogin") }}
              </h2>
              <p class="text-sm text-gray-600 mt-2">
                {{ $t("page.login.common.loginBenefits") }}
              </p>
            </div>
          </template>

          <!-- Content -->
          <div class="space-y-4">
            <div class="text-center space-y-3">
              <NButton type="primary" size="large" block @click="handleLogin" :loading="loading">
                {{ $t("page.login.common.login") }}
              </NButton>

              <NButton quaternary size="medium" block @click="handleDismiss">
                {{ $t("page.login.common.continueAsGuest") }}
              </NButton>
            </div>

            <!-- Context-aware benefits -->
            <div v-if="routeBenefits.length > 0" class="mt-4 p-3 bg-blue-50 rounded-lg" :class="[direction]">
              <h4 class="text-sm font-medium text-blue-900 mb-2">
                {{ $t("page.login.common.unlockFeatures") }}
              </h4>
              <ul class="text-xs text-blue-700 space-y-1">
                <li v-for="benefit in routeBenefits" :key="benefit">
                  • {{ benefit }}
                </li>
              </ul>
            </div>
          </div>
        </NCard>
      </div>
    </div>
  </Teleport>
</template>
